import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import PasswordProtection from "@/components/PasswordProtection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { DollarSign, AlertTriangle, Clock, CheckCircle, Download, Filter, MessageCircle, Calendar, Receipt, Plus, Trash2, Edit, CreditCard, Link2, Copy, Send, Bot } from "lucide-react";
import { toast } from "sonner";


const COLORS = {
  vencidas: '#ef4444',
  a_vencer: '#f59e0b',
  pagas: '#10b981'
};

const CATEGORIAS_DESPESA = [
  { value: 'aluguel', label: 'Aluguel' },
  { value: 'material', label: 'Material Esportivo' },
  { value: 'salario', label: 'Salários' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'agua_luz', label: 'Água/Luz' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'outros', label: 'Outros' }
];

// Função para gerar os últimos 12 meses
const generateMonthOptions = () => {
  const months = [];
  const now = new Date();
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  
  return months;
};

export default function Mensalidades({ onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos"); // todos, pagas, vencidas, a_vencer
  const [mesSelecionado, setMesSelecionado] = useState("todos"); // para filtrar mensalidades pagas
  const [whatsappTemplates, setWhatsappTemplates] = useState({
    a_vencer: "",
    vencida: ""
  });
  
  // Estados para despesas
  const [despesas, setDespesas] = useState([]);
  const [despesasResumo, setDespesasResumo] = useState(null);
  const [showDespesaDialog, setShowDespesaDialog] = useState(false);
  const [editingDespesa, setEditingDespesa] = useState(null);
  const [novaDespesa, setNovaDespesa] = useState({
    descricao: '',
    valor: '',
    categoria: 'outros',
    data: new Date().toISOString().split('T')[0],
    observacao: ''
  });

  // Estados para Mercado Pago
  const [mercadoPagoStatus, setMercadoPagoStatus] = useState({ configured: false });
  const [gerandoLink, setGerandoLink] = useState(null);
  const [linkPagamento, setLinkPagamento] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const monthOptions = useMemo(() => generateMonthOptions(), []);

  useEffect(() => {
    fetchDashboard();
    fetchWhatsappTemplates();
    fetchDespesas();
    checkMercadoPagoStatus();
  }, []);

  const checkMercadoPagoStatus = async () => {
    try {
      const response = await api.get('/perfil/mercadopago/status');
      setMercadoPagoStatus(response.data);
    } catch (error) {
      console.log("Mercado Pago não configurado");
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/mensalidades/dashboard');
      setDashboard(response.data);
    } catch (error) {
      toast.error("Erro ao carregar mensalidades");
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsappTemplates = async () => {
    try {
      const response = await api.get('/perfil/whatsapp-templates');
      setWhatsappTemplates(response.data);
    } catch (error) {
      console.error("Erro ao carregar templates WhatsApp");
    }
  };

  // Funções de Despesas
  const fetchDespesas = async () => {
    try {
      const [despesasRes, resumoRes] = await Promise.all([
        api.get('/despesas'),
        api.get('/despesas/resumo')
      ]);
      setDespesas(despesasRes.data);
      setDespesasResumo(resumoRes.data);
    } catch (error) {
      console.error("Erro ao carregar despesas");
    }
  };

  const handleSaveDespesa = async () => {
    if (!novaDespesa.descricao || !novaDespesa.valor || !novaDespesa.data) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      if (editingDespesa) {
        await api.put(`/despesas/${editingDespesa.id}`, {
          ...novaDespesa,
          valor: parseFloat(novaDespesa.valor)
        });
        toast.success("Despesa atualizada!");
      } else {
        await api.post('/despesas', {
          ...novaDespesa,
          valor: parseFloat(novaDespesa.valor)
        });
        toast.success("Despesa adicionada!");
      }
      
      setShowDespesaDialog(false);
      setEditingDespesa(null);
      setNovaDespesa({
        descricao: '',
        valor: '',
        categoria: 'outros',
        data: new Date().toISOString().split('T')[0],
        observacao: ''
      });
      fetchDespesas();
    } catch (error) {
      toast.error("Erro ao salvar despesa");
    }
  };

  const handleEditDespesa = (despesa) => {
    setEditingDespesa(despesa);
    setNovaDespesa({
      descricao: despesa.descricao,
      valor: despesa.valor.toString(),
      categoria: despesa.categoria,
      data: despesa.data,
      observacao: despesa.observacao || ''
    });
    setShowDespesaDialog(true);
  };

  const handleDeleteDespesa = async (despesaId) => {
    if (!confirm("Tem certeza que deseja excluir esta despesa?")) return;
    
    try {
      await api.delete(`/despesas/${despesaId}`);
      toast.success("Despesa excluída!");
      fetchDespesas();
    } catch (error) {
      toast.error("Erro ao excluir despesa");
    }
  };

  const marcarComoPaga = async (alunoId) => {
    try {
      await api.post(`/mensalidades/marcar-paga/${alunoId}`, {});
      toast.success("Mensalidade marcada como paga!");
      fetchDashboard();
    } catch (error) {
      toast.error("Erro ao marcar mensalidade");
    }
  };

  const marcarComoPendente = async (alunoId) => {
    try {
      await api.post(`/mensalidades/marcar-pendente/${alunoId}`, {});
      toast.success("Mensalidade marcada como pendente!");
      fetchDashboard();
    } catch (error) {
      toast.error("Erro ao marcar mensalidade");
    }
  };

  const exportarExcel = async () => {
    try {
      
      
      // Determinar qual mês exportar
      let mesParam = '';
      if (mesSelecionado && mesSelecionado !== "todos") {
        mesParam = `?mes=${mesSelecionado}`;
      }
      
      const response = await api.get(`/export/mensalidades/excel${mesParam}`, {
        responseType: 'blob'
      });
      
      // Nome do arquivo baseado no mês
      let filename = 'relatorio_mensalidades.xlsx';
      if (mesSelecionado && mesSelecionado !== "todos") {
        const [ano, mes] = mesSelecionado.split('-');
        filename = `relatorio_mensalidades_${ano}_${mes}.xlsx`;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      const mesLabel = mesSelecionado === "todos" 
        ? "mês atual" 
        : monthOptions.find(m => m.value === mesSelecionado)?.label || mesSelecionado;
      toast.success(`Relatório de ${mesLabel} exportado com sucesso!`);
    } catch (error) {
      toast.error("Erro ao exportar relatório");
    }
  };

  // Filtrar mensalidades pagas por mês e ordenar alfabeticamente
  const mensalidadesPagasFiltradas = useMemo(() => {
    if (!dashboard?.pagas) return [];
    
    let filtered;
    if (mesSelecionado === "todos") {
      filtered = dashboard.pagas;
    } else {
      const [ano, mes] = mesSelecionado.split('-').map(Number);
      
      filtered = dashboard.pagas.filter(item => {
        if (!item.data_pagamento) return false;
        const dataPagamento = new Date(item.data_pagamento);
        return dataPagamento.getFullYear() === ano && dataPagamento.getMonth() + 1 === mes;
      });
    }
    
    // Ordenar alfabeticamente
    return filtered.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
  }, [dashboard?.pagas, mesSelecionado]);

  // Calcular totais filtrados por mês
  const totaisFiltrados = useMemo(() => {
    const valorPagas = mensalidadesPagasFiltradas.reduce((acc, item) => acc + (item.valor || 0), 0);
    return {
      quantidade: mensalidadesPagasFiltradas.length,
      valor: valorPagas
    };
  }, [mensalidadesPagasFiltradas]);

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  if (!dashboard) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Erro ao carregar dados</div></Layout>;
  }

  // Dados do gráfico do mês vigente (apenas a vencer, pendentes e atrasadas)
  const mesVigenteData = [
    { name: 'A Vencer', value: dashboard.total_a_vencer || 0, color: COLORS.a_vencer },
    { name: 'Vencidas', value: dashboard.total_vencidas || 0, color: COLORS.vencidas }
  ];

  const pieData = [
    { name: 'Pagas', value: dashboard.total_pagas || 0, color: COLORS.pagas },
    { name: 'A Vencer', value: dashboard.total_a_vencer || 0, color: COLORS.a_vencer },
    { name: 'Vencidas', value: dashboard.total_vencidas || 0, color: COLORS.vencidas }
  ];

  const barData = [
    { name: 'Pagas', value: dashboard.total_pagas || 0 },
    { name: 'Pendentes', value: (dashboard.total_a_vencer || 0) + (dashboard.total_vencidas || 0) }
  ];

  const valorData = [
    { name: 'Recebido', value: dashboard.valor_total_pagas || 0, fill: '#10b981' },
    { name: 'A Receber', value: (dashboard.valor_total_a_vencer || 0) + (dashboard.valor_total_vencidas || 0), fill: '#f59e0b' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const enviarCobrancaWhatsApp = (mensalidade) => {
    const telefone = mensalidade.telefone.replace(/\D/g, '');
    
    let template = mensalidade.dias_atraso 
      ? whatsappTemplates.vencida 
      : whatsappTemplates.a_vencer;
    
    if (!template) {
      template = mensalidade.dias_atraso
        ? "Olá {responsavel}! 👋\n\nEste é um lembrete sobre a mensalidade de *{aluno}*.\n\n⚠️ *Mensalidade VENCIDA*\nVencimento: dia {dia_vencimento}\nDias de atraso: {dias_atraso} dias\n\nValor: *{valor}*\n\nPor favor, realize o pagamento o quanto antes para manter a matrícula ativa.\n\nQualquer dúvida, estamos à disposição!"
        : "Olá {responsavel}! 👋\n\nEste é um lembrete sobre a mensalidade de *{aluno}*.\n\n📅 *Mensalidade a vencer*\nVencimento: dia {dia_vencimento}\nDias restantes: {dias_faltam} dias\n\nValor: *{valor}*\n\nPor favor, realize o pagamento para manter a matrícula ativa.\n\nQualquer dúvida, estamos à disposição!";
    }
    
    const mensagem = template
      .replace(/{responsavel}/g, mensalidade.responsavel)
      .replace(/{aluno}/g, mensalidade.aluno_nome)
      .replace(/{dia_vencimento}/g, mensalidade.dia_vencimento)
      .replace(/{dias_atraso}/g, mensalidade.dias_atraso || '')
      .replace(/{dias_faltam}/g, mensalidade.dias_faltam || '')
      .replace(/{valor}/g, formatCurrency(mensalidade.valor));
    
    const whatsappLink = `https://wa.me/55${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappLink, '_blank');
    toast.success("Link do WhatsApp aberto!");
  };

  const gerarLinkPagamento = async (mensalidade) => {
    if (!mercadoPagoStatus.configured) {
      toast.error("Mercado Pago não configurado. Configure as credenciais no painel.");
      return;
    }

    setGerandoLink(mensalidade.aluno_id);
    
    try {
      const mesAtual = new Date().toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
      
      const response = await api.post('/pagamentos/mensalidade/gerar-link', {
        aluno_id: mensalidade.aluno_id,
        mensalidade_id: `${mensalidade.aluno_id}_${Date.now()}`,
        valor: mensalidade.valor,
        mes_referencia: mesAtual,
        email_responsavel: null,
        nome_responsavel: mensalidade.responsavel
      });

      setLinkPagamento({
        ...response.data,
        aluno_nome: mensalidade.aluno_nome,
        responsavel: mensalidade.responsavel,
        telefone: mensalidade.telefone,
        valor: mensalidade.valor
      });
      setShowLinkDialog(true);
      toast.success("Link de pagamento gerado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar link de pagamento");
    } finally {
      setGerandoLink(null);
    }
  };

  const copiarLink = async (link) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        toast.success("Link copiado!");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success("Link copiado!");
      }
    } catch {
      toast.info(`Copie o link: ${link}`);
    }
  };

  const enviarLinkWhatsApp = () => {
    if (!linkPagamento) return;
    
    const telefone = linkPagamento.telefone.replace(/\D/g, '');
    const mensagem = `Olá ${linkPagamento.responsavel}! 👋\n\nSegue o link para pagamento da mensalidade de *${linkPagamento.aluno_nome}*:\n\n💰 Valor: *${formatCurrency(linkPagamento.valor)}*\n\n🔗 Link de pagamento:\n${linkPagamento.payment_url}\n\n✅ Você pode pagar via PIX, Boleto ou Cartão!\n\nQualquer dúvida, estamos à disposição!`;
    
    const whatsappLink = `https://wa.me/55${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappLink, '_blank');
    toast.success("WhatsApp aberto com link de pagamento!");
  };

  const mesAtual = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <PasswordProtection page="mensalidades" onLogout={onLogout}>
      <Layout onLogout={onLogout}>
        <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Financeiro</h1>
            <p className="text-gray-600">Mensalidades e Despesas</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/cobranca/em-massa'}
              data-testid="btn-cobranca-massa"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-1 sm:flex-none"
            >
              <Send className="w-4 h-4 mr-1.5" />
              Cobrar em massa
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/configuracoes/cobranca-automatica'}
              data-testid="btn-cobranca-config"
              className="flex-1 sm:flex-none border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Bot className="w-4 h-4 mr-1.5" />
              Configurar cobrança automática
            </Button>
          </div>
        </div>

        <Tabs defaultValue="mensalidades" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="mensalidades" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Mensalidades
            </TabsTrigger>
            <TabsTrigger value="despesas" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Despesas
            </TabsTrigger>
          </TabsList>

          {/* ========== ABA MENSALIDADES ========== */}
          <TabsContent value="mensalidades" className="space-y-6">
            {/* Banner Mercado Pago */}
            {mercadoPagoStatus.configured ? (
              <Alert className="bg-blue-50 border-blue-200">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  <strong>Mercado Pago ativo!</strong> Você pode gerar links de pagamento e receber via PIX, Boleto ou Cartão.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm flex items-center justify-between flex-wrap gap-2">
                  <span><strong>Mercado Pago não configurado.</strong> Configure para receber pagamentos online.</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => window.location.href = '/configuracoes/pagamento'}
                  >
                    Configurar Agora
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end items-center flex-wrap gap-4">
              <div className="flex gap-3 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-600" />
                  <Select value={filtro} onValueChange={setFiltro}>
                    <SelectTrigger className="w-[180px]" data-testid="filtro-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os Alunos</SelectItem>
                      <SelectItem value="pagas">Pagas</SelectItem>
                      <SelectItem value="vencidas">Vencidas</SelectItem>
                      <SelectItem value="a_vencer">A Vencer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <Select value={mesSelecionado} onValueChange={setMesSelecionado}>
                    <SelectTrigger className="w-[180px]" data-testid="filtro-mes-export">
                      <SelectValue placeholder="Mês do relatório" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Mês Atual</SelectItem>
                      {monthOptions.map((month) => (
                        <SelectItem key={month.value} value={month.value}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="exportar-excel-button"
                  onClick={exportarExcel}
                  className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              </div>
            </div>

        {/* Alerta de TODAS as Mensalidades Atrasadas - sempre visível se houver */}
        {dashboard.vencidas.length > 0 && (
          <Alert className="border-red-300 bg-red-50" data-testid="alert-todas-vencidas">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800 font-bold text-lg">
              ⚠️ {dashboard.vencidas.length} Mensalidade{dashboard.vencidas.length > 1 ? 's' : ''} Atrasada{dashboard.vencidas.length > 1 ? 's' : ''}!
            </AlertTitle>
            <AlertDescription className="text-red-700">
              <div className="mt-2 space-y-1">
                {[...dashboard.vencidas].sort((a, b) => (a.aluno_nome || '').localeCompare(b.aluno_nome || '', 'pt-BR')).map((item) => (
                  <div key={item.aluno_id} className="flex items-center justify-between py-1 border-b border-red-200 last:border-0">
                    <span>
                      <strong>{item.aluno_nome}</strong> - {formatCurrency(item.valor)} 
                      <span className="text-red-600 ml-2">({item.dias_atraso} dias de atraso)</span>
                    </span>
                    <Button
                      onClick={() => enviarCobrancaWhatsApp(item)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Cobrar
                    </Button>
                    {mercadoPagoStatus.configured && (
                      <Button
                        onClick={() => gerarLinkPagamento(item)}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs ml-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                        disabled={gerandoLink === item.aluno_id}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        {gerandoLink === item.aluno_id ? "..." : "Pagar"}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-red-300 font-bold">
                Total em atraso: {formatCurrency(dashboard.valor_total_vencidas)}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Total Esperado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600" data-testid="valor-total">{formatCurrency(dashboard.valor_total_esperado)}</div>
              <div className="text-xs text-gray-500 mt-1">{dashboard.total_alunos} alunos</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Recebido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600" data-testid="valor-pagas">{formatCurrency(dashboard.valor_total_pagas)}</div>
              <div className="text-xs text-gray-500 mt-1">{dashboard.total_pagas} pagas</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                A Receber
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600" data-testid="valor-a-vencer">{formatCurrency(dashboard.valor_total_a_vencer)}</div>
              <div className="text-xs text-gray-500 mt-1">{dashboard.total_a_vencer} a vencer</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Vencidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600" data-testid="valor-vencidas">{formatCurrency(dashboard.valor_total_vencidas)}</div>
              <div className="text-xs text-gray-500 mt-1">{dashboard.total_vencidas} vencidas</div>
            </CardContent>
          </Card>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gráfico do Mês Vigente */}
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Mês Vigente
              </CardTitle>
              <CardDescription>{mesAtual.charAt(0).toUpperCase() + mesAtual.slice(1)} - Pendências</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={mesVigenteData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => value > 0 ? `${name}: ${value} (${(percent * 100).toFixed(0)}%)` : ''}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {mesVigenteData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-2 text-sm text-gray-600">
                Total pendente: <strong className="text-orange-600">{(dashboard.total_a_vencer || 0) + (dashboard.total_vencidas || 0)}</strong> mensalidades
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Distribuição Geral</CardTitle>
              <CardDescription>Status de todas as mensalidades</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Valores Financeiros (R$)</CardTitle>
              <CardDescription>Receitas recebidas x a receber</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={valorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Bar dataKey="value">
                    {valorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Mensalidades Vencidas - Lista Detalhada */}
        {(filtro === "todos" || filtro === "vencidas") && dashboard.vencidas.length > 0 && (
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <CardTitle className="text-red-800">Mensalidades Vencidas - Detalhes</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...dashboard.vencidas].sort((a, b) => (a.aluno_nome || '').localeCompare(b.aluno_nome || '', 'pt-BR')).map((item) => (
                  <Card key={item.aluno_id} className="border-red-200 bg-red-50" data-testid={`vencida-${item.aluno_id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-base sm:text-lg break-words">{item.aluno_nome}</h4>
                          <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                            <div className="text-lg sm:text-xl font-bold text-red-600">{formatCurrency(item.valor)}</div>
                            <div><strong>Vencimento:</strong> Dia {item.dia_vencimento}</div>
                            <div className="text-red-600"><strong>Atraso:</strong> {item.dias_atraso} dias</div>
                            <div className="break-words"><strong>Responsável:</strong> {item.responsavel} - {item.telefone}</div>
                          </div>
                        </div>
                        <div className="flex flex-row sm:flex-col gap-2 shrink-0 flex-wrap">
                          <Button
                            onClick={() => enviarCobrancaWhatsApp(item)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 whitespace-nowrap flex-1 sm:flex-none"
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            WhatsApp
                          </Button>
                          {mercadoPagoStatus.configured && (
                            <Button
                              onClick={() => gerarLinkPagamento(item)}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap flex-1 sm:flex-none"
                              disabled={gerandoLink === item.aluno_id}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              {gerandoLink === item.aluno_id ? "Gerando..." : "Link Pagar"}
                            </Button>
                          )}
                          <Button
                            data-testid={`marcar-paga-${item.aluno_id}`}
                            onClick={() => marcarComoPaga(item.aluno_id)}
                            size="sm"
                            variant="outline"
                            className="flex-1 sm:flex-none"
                          >
                            Marcar Paga
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mensalidades A Vencer */}
        {(filtro === "todos" || filtro === "a_vencer") && dashboard.a_vencer.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <CardTitle>Mensalidades A Vencer</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...dashboard.a_vencer].sort((a, b) => (a.aluno_nome || '').localeCompare(b.aluno_nome || '', 'pt-BR')).map((item) => (
                  <div key={item.aluno_id} className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center p-3 border rounded-lg" data-testid={`a-vencer-${item.aluno_id}`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium break-words">{item.aluno_nome}</h4>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {formatCurrency(item.valor)} • Vence dia {item.dia_vencimento} • Faltam {item.dias_faltam} dias
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        onClick={() => enviarCobrancaWhatsApp(item)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                      <Button
                        data-testid={`marcar-paga-avencer-${item.aluno_id}`}
                        onClick={() => marcarComoPaga(item.aluno_id)}
                        size="sm"
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-50 flex-1 sm:flex-none"
                      >
                        Marcar Paga
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mensalidades Pagas - Filtrado pelo mês selecionado no cabeçalho */}
        {(filtro === "todos" || filtro === "pagas") && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <CardTitle>Mensalidades Pagas</CardTitle>
                  {mesSelecionado !== "todos" && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800 ml-2">
                      {monthOptions.find(m => m.value === mesSelecionado)?.label}
                    </Badge>
                  )}
                </div>
                {mesSelecionado !== "todos" && (
                  <Badge variant="outline" className="text-green-700 border-green-300">
                    {totaisFiltrados.quantidade} mensalidade{totaisFiltrados.quantidade !== 1 ? 's' : ''} • {formatCurrency(totaisFiltrados.valor)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {mensalidadesPagasFiltradas.length > 0 ? (
                <div className="space-y-2">
                  {mensalidadesPagasFiltradas.map((item) => (
                    <div key={item.aluno_id} className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center p-3 border rounded-lg bg-green-50" data-testid={`paga-${item.aluno_id}`}>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium break-words">{item.aluno_nome}</h4>
                        <div className="text-xs sm:text-sm text-gray-600">
                          <span className="text-green-600 font-medium">{formatCurrency(item.valor)}</span> • 
                          Pago em: {item.data_pagamento ? new Date(item.data_pagamento).toLocaleDateString('pt-BR') : 'N/A'}
                        </div>
                      </div>
                      <Button
                        data-testid={`marcar-pendente-${item.aluno_id}`}
                        onClick={() => marcarComoPendente(item.aluno_id)}
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                      >
                        Marcar como Pendente
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {mesSelecionado === "todos" 
                    ? "Nenhuma mensalidade paga encontrada" 
                    : `Nenhuma mensalidade paga em ${monthOptions.find(m => m.value === mesSelecionado)?.label || mesSelecionado}`}
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </TabsContent>

          {/* ========== ABA DESPESAS ========== */}
          <TabsContent value="despesas" className="space-y-6">
            {/* Resumo de Despesas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Total de Despesas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {formatCurrency(despesasResumo?.total || 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {despesasResumo?.quantidade || 0} despesa(s)
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Receita (Pagas)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(dashboard?.valor_total_pagas || 0)}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Saldo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${
                    ((dashboard?.valor_total_pagas || 0) - (despesasResumo?.total || 0)) >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency((dashboard?.valor_total_pagas || 0) - (despesasResumo?.total || 0))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Receita - Despesas</div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Adicionar Despesa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => {
                      setEditingDespesa(null);
                      setNovaDespesa({
                        descricao: '',
                        valor: '',
                        categoria: 'outros',
                        data: new Date().toISOString().split('T')[0],
                        observacao: ''
                      });
                      setShowDespesaDialog(true);
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="nova-despesa-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Despesa
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Despesas por Categoria */}
            {despesasResumo?.por_categoria && Object.keys(despesasResumo.por_categoria).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Despesas por Categoria</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(despesasResumo.por_categoria).map(([cat, valor]) => (
                      <div key={cat} className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-600 capitalize">
                          {CATEGORIAS_DESPESA.find(c => c.value === cat)?.label || cat}
                        </div>
                        <div className="text-lg font-bold text-gray-800">
                          {formatCurrency(valor)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de Despesas */}
            <Card>
              <CardHeader>
                <CardTitle>Lista de Despesas</CardTitle>
              </CardHeader>
              <CardContent>
                {despesas.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nenhuma despesa cadastrada
                  </div>
                ) : (
                  <div className="space-y-3">
                    {despesas.map((despesa) => (
                      <div 
                        key={despesa.id} 
                        className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 border rounded-lg hover:bg-gray-50"
                        data-testid={`despesa-${despesa.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium break-words">{despesa.descricao}</h4>
                          <div className="text-xs sm:text-sm text-gray-600 flex flex-wrap items-center gap-2">
                            <Badge variant="outline">
                              {CATEGORIAS_DESPESA.find(c => c.value === despesa.categoria)?.label || despesa.categoria}
                            </Badge>
                            <span>{new Date(despesa.data).toLocaleDateString('pt-BR')}</span>
                            {despesa.observacao && (
                              <span className="text-gray-400 break-words">• {despesa.observacao}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                          <span className="text-base sm:text-lg font-bold text-red-600">
                            {formatCurrency(despesa.valor)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditDespesa(despesa)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteDespesa(despesa.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog para Nova/Editar Despesa */}
        <Dialog open={showDespesaDialog} onOpenChange={setShowDespesaDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDespesa ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle>
              <DialogDescription>
                Preencha os dados da despesa
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input
                  value={novaDespesa.descricao}
                  onChange={(e) => setNovaDespesa({...novaDespesa, descricao: e.target.value})}
                  placeholder="Ex: Compra de bolas"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={novaDespesa.valor}
                    onChange={(e) => setNovaDespesa({...novaDespesa, valor: e.target.value})}
                    placeholder="0,00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input
                    type="date"
                    value={novaDespesa.data}
                    onChange={(e) => setNovaDespesa({...novaDespesa, data: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select 
                  value={novaDespesa.categoria} 
                  onValueChange={(value) => setNovaDespesa({...novaDespesa, categoria: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS_DESPESA.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Observação</Label>
                <Textarea
                  value={novaDespesa.observacao}
                  onChange={(e) => setNovaDespesa({...novaDespesa, observacao: e.target.value})}
                  placeholder="Observações adicionais..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDespesaDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveDespesa} className="bg-purple-600 hover:bg-purple-700">
                {editingDespesa ? 'Salvar' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog do Link de Pagamento */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Link de Pagamento Gerado
              </DialogTitle>
              <DialogDescription>
                {linkPagamento && (
                  <span>Pagamento para <strong>{linkPagamento.aluno_nome}</strong> - {formatCurrency(linkPagamento.valor)}</span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {linkPagamento && (
              <div className="space-y-4 py-4">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Link pronto! O responsável pode pagar via <strong>PIX, Boleto ou Cartão</strong>.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <Label>Link de Pagamento:</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={linkPagamento.payment_url} 
                      readOnly 
                      className="text-sm bg-gray-50"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copiarLink(linkPagamento.payment_url)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={enviarLinkWhatsApp}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Enviar via WhatsApp
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1"
                    onClick={() => window.open(linkPagamento.payment_url, '_blank')}
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Abrir Link
                  </Button>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
    </PasswordProtection>
  );
}
